import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Search, Filter, X, Eye, RefreshCw, ChevronDown, ChevronUp, AlertCircle, Loader2, Wifi, WifiOff, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { liveAnnouncementsAPI, stocksAPI, indexesAPI, profileAPI, getErrorMessage } from '../../lib/api';
import { toast } from '../../hooks/use-toast';
import { useLiveAnnouncementSocket } from '../../hooks/useLiveAnnouncementSocket';
import { UpgradeOverlay } from '../../components/UpgradeOverlay';
import { useAccess } from '../../hooks/useAccess';
import { RESULTS_FILINGS_LABELS, isResultsFilingsAnnouncement } from '../../constants/liveIca';

const LiveAnnouncementsContent = () => {
  // Get access info for Live ICA
  const { liveIca, isFreeTier } = useAccess();
  const isBasicTier = liveIca.tier === 'basic';
  const canCustomize = liveIca.canCustomize;
  const defaultIndex = liveIca.defaultIndex;

  const [filters, setFilters] = useState({
    companyName: '',
    category: '',
    startDate: '',
    endDate: ''
  });

  // Tracks the last submitted filters (used by API calls)
  const [appliedFilters, setAppliedFilters] = useState({
    companyName: '',
    category: '',
    startDate: '',
    endDate: ''
  });

  // API states
  const [announcements, setAnnouncements] = useState([]);
  const [selectedStocks, setSelectedStocks] = useState([]);
  const [selectedIndexes, setSelectedIndexes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectionsLoading, setSelectionsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0
  });
  const [todaysCount, setTodaysCount] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [categories, setCategories] = useState([]);

  // Null until the selection summary loads; see fetchAccessibleStocks.
  const [hasAccessibleStocks, setHasAccessibleStocks] = useState(null);
  // Scrip codes the user can see, kept in a ref so the socket predicate stays stable.
  const accessibleScripCodesRef = useRef(new Set());

  // Results & Filings preference (the five public subcategories)
  const [resultsFilingsEnabled, setResultsFilingsEnabled] = useState(true);
  const [resultsFilingsSaving, setResultsFilingsSaving] = useState(false);
  const [showResultsFilingsConfirm, setShowResultsFilingsConfirm] = useState(false);

  // Autocomplete states
  const [showCompanySuggestions, setShowCompanySuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  
  const lastAnnouncementIdRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const filtersRef = useRef(filters);
  const appliedFiltersRef = useRef(appliedFilters);
  const companyInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  
  // Keep filtersRef in sync with filters state (UI only)
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Keep appliedFiltersRef in sync with appliedFilters state (used by API calls)
  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);
  
  // Ref to store fetchAnnouncements to avoid circular dependency
  const fetchAnnouncementsRef = useRef(null);

  // Fetch user selections (stocks and indexes)
  const fetchUserSelections = useCallback(async () => {
    setSelectionsLoading(true);
    try {
      const [stocksRes, indexesRes] = await Promise.all([
        stocksAPI.getSelectedStocks(),
        indexesAPI.getSelectedIndexes()
      ]);
      
      setSelectedStocks(stocksRes.data?.data?.selected_stocks || []);
      setSelectedIndexes(indexesRes.data?.data?.selected_indexes || []);
    } catch (error) {
      console.error('Error fetching selections:', error);
    } finally {
      setSelectionsLoading(false);
    }
  }, []);

  // Whether the backend can resolve any stock for this user at all (explicit
  // selections or the basic-tier default index). Null until known, so the
  // confirmation never fires on incomplete data.
  const fetchAccessibleStocks = useCallback(async () => {
    try {
      const response = await liveAnnouncementsAPI.getUserSelections();
      const summary = response.data?.data;
      if (summary && typeof summary.has_selections === 'boolean') {
        setHasAccessibleStocks(summary.has_selections);
      }
      // Scrip codes drive the toast filter: with the preference off we still
      // surface the five types for companies the user actually selected.
      const codes = (summary?.total_accessible_stocks_list || [])
        .map((stock) => stock.scrip_code)
        .filter((code) => code !== null && code !== undefined);
      accessibleScripCodesRef.current = new Set(codes.map(Number));
    } catch (error) {
      console.error('Error fetching selection summary:', error);
    }
  }, []);

  // Fetch announcements with filters
  const fetchAnnouncements = useCallback(async (page = 1, isWebSocketRefresh = false) => {
    setLoading(true);
    try {
      // Use appliedFiltersRef — only updated when user explicitly clicks Filter or Clear
      const currentFilters = appliedFiltersRef.current;
      const params = {
        page,
        ...(currentFilters.companyName && { company_name: currentFilters.companyName }),
        ...(currentFilters.category && { category_name: currentFilters.category }),
        ...(currentFilters.startDate && { start_date: currentFilters.startDate }),
        ...(currentFilters.endDate && { end_date: currentFilters.endDate })
      };

      console.log('Fetching announcements with params:', params, 'isWebSocketRefresh:', isWebSocketRefresh);
      const response = await liveAnnouncementsAPI.getAnnouncements(params);
      const data = response.data;
      console.log('API Response:', data?.data?.length, 'announcements, pagination:', data?.pagination);
      
      // Update today's count from pagination object
      if (data?.pagination?.todaysCount !== undefined) {
        setTodaysCount(data.pagination.todaysCount);
      }
      
      if (data?.data) {
        // Check for new announcements (only for WebSocket-triggered refreshes)
        if (isWebSocketRefresh && data.data.length > 0) {
          const latestId = data.data[0]?.id;
          if (lastAnnouncementIdRef.current && latestId !== lastAnnouncementIdRef.current) {
            const newCount = data.data.findIndex(a => a.id === lastAnnouncementIdRef.current);
            if (newCount > 0) {
              toast.info({
                title: 'New Announcements',
                description: `${newCount} new announcement${newCount > 1 ? 's' : ''} received`
              });
            }
          }
        }
        // Always update the last announcement ID
        if (data.data.length > 0) {
          lastAnnouncementIdRef.current = data.data[0]?.id;
          isInitialLoadRef.current = false;
        }
        
        setAnnouncements(data.data);
        setPagination({
          page: data.pagination?.page || page,
          pageSize: data.pagination?.pageSize || 20,
          total: data.pagination?.total || data.data.length,
          totalPages: data.pagination?.totalPages || 1
        });
        
      } else {
        setAnnouncements([]);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
      toast.error({
        title: 'Error',
        description: getErrorMessage(error)
      });
      setAnnouncements([]);
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies - uses filtersRef.current instead

  // Store fetchAnnouncements in ref for WebSocket callback
  useEffect(() => {
    fetchAnnouncementsRef.current = fetchAnnouncements;
  }, [fetchAnnouncements]);

  // WebSocket for real-time announcements
  const handleNewAnnouncement = useCallback((data) => {
    console.log('WebSocket: New announcement received, refreshing list...', data);
    // Refresh announcements when a new one arrives
    if (fetchAnnouncementsRef.current) {
      // Small delay to ensure backend has processed the announcement
      setTimeout(() => {
        console.log('WebSocket: Calling fetchAnnouncements now');
        fetchAnnouncementsRef.current(1, true); // Pass isWebSocketRefresh=true
      }, 1500);
    }
  }, []);

  // The server broadcasts the public subcategories to every socket, so a user
  // who switched Results & Filings off filters them out here. Read through refs
  // so the predicate stays stable and does not tear down the socket on toggle.
  const resultsFilingsEnabledRef = useRef(resultsFilingsEnabled);
  useEffect(() => {
    resultsFilingsEnabledRef.current = resultsFilingsEnabled;
  }, [resultsFilingsEnabled]);

  const shouldAcceptAnnouncement = useCallback((announcement) => {
    if (resultsFilingsEnabledRef.current) return true;
    if (!isResultsFilingsAnnouncement(announcement)) return true;

    // Preference off: keep it only when the company is one the user selected.
    const scripCode = Number(announcement?.scrip_code);
    if (!Number.isFinite(scripCode)) return false;
    return accessibleScripCodesRef.current.has(scripCode);
  }, []);

  const { isConnected, connectionState } = useLiveAnnouncementSocket({
    enabled: true,
    onNewAnnouncement: handleNewAnnouncement,
    shouldAccept: shouldAcceptAnnouncement
  });

  // Fetch distinct categories from backend on mount
  const fetchCategories = useCallback(async () => {
    try {
      const response = await liveAnnouncementsAPI.getCategories();
      const data = response.data?.data;
      if (Array.isArray(data) && data.length > 0) {
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  // Current Results & Filings preference lives on the user's profile
  const fetchResultsFilingsPreference = useCallback(async () => {
    try {
      const response = await profileAPI.getProfile();
      const enabled = response.data?.data?.results_filings_enabled;
      if (typeof enabled === 'boolean') {
        setResultsFilingsEnabled(enabled);
      }
    } catch (error) {
      // Leave the toggle on its default; the feed still renders correctly.
      console.error('Error fetching results & filings preference:', error);
    }
  }, []);

  // Initial load - only runs once on mount
  useEffect(() => {
    fetchUserSelections();
    fetchAccessibleStocks();
    fetchAnnouncements();
    fetchCategories();
    fetchResultsFilingsPreference();
  }, []);

  // Turning the toggle off with nothing accessible leaves an empty feed, so that
  // is the one case we interrupt with a confirmation.
  //
  // hasAccessibleStocks comes straight from the backend's
  // get_user_accessible_stocks(), which already folds in the basic-tier default
  // index. Neither the selection lists nor the tier can answer this on their
  // own: a basic-tier user whose plan has no default_index configured resolves
  // to zero accessible stocks, and liveIca.defaultIndex reads null in that case
  // as well as when the fallback is working.
  const feedWouldBeEmpty = hasAccessibleStocks === false;

  const saveResultsFilingsPreference = useCallback(async (nextValue) => {
    const previousValue = !nextValue;
    setResultsFilingsSaving(true);
    // Optimistic: flip now, roll back if the request fails.
    setResultsFilingsEnabled(nextValue);

    try {
      await profileAPI.updateProfile({ results_filings_enabled: nextValue });
      fetchAnnouncements(1);
      fetchCategories();
      toast.success({
        title: nextValue ? 'Results & Filings on' : 'Results & Filings off',
        description: nextValue
          ? 'These announcements are back in your feed and notifications.'
          : 'Hidden for companies outside your watchlist. Your stocks are unaffected.'
      });
    } catch (error) {
      setResultsFilingsEnabled(previousValue);
      toast.error({
        title: 'Could not save your preference',
        description: getErrorMessage(error)
      });
    } finally {
      setResultsFilingsSaving(false);
    }
  }, [fetchAnnouncements, fetchCategories]);

  const handleResultsFilingsToggle = () => {
    if (resultsFilingsSaving) return;

    const nextValue = !resultsFilingsEnabled;
    if (!nextValue && feedWouldBeEmpty) {
      setShowResultsFilingsConfirm(true);
      return;
    }

    saveResultsFilingsPreference(nextValue);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilters = () => {
    // Commit UI filters to appliedFilters so fetchAnnouncements picks them up
    setAppliedFilters({ ...filters });
    appliedFiltersRef.current = { ...filtersRef.current };
    fetchAnnouncements(1);
  };

  const handleClearFilters = () => {
    const empty = { companyName: '', category: '', startDate: '', endDate: '' };
    setFilters(empty);
    setAppliedFilters(empty);
    filtersRef.current = empty;
    appliedFiltersRef.current = empty;
    fetchAnnouncements(1);
  };

  const handleRefresh = () => {
    fetchUserSelections();
    fetchAccessibleStocks();
    fetchAnnouncements(pagination.page);
    toast.info({
      title: 'Refreshing',
      description: 'Fetching latest announcements...'
    });
  };

  const handleReadPDF = async (announcement) => {
    if (announcement.pdf_url) {
      window.open(announcement.pdf_url, '_blank');
    } else {
      try {
        const response = await liveAnnouncementsAPI.getPdfUrl(announcement.id);
        if (response.data?.data?.pdf_url) {
          window.open(response.data.data.pdf_url, '_blank');
        } else {
          toast.warning({
            title: 'No PDF Available',
            description: 'PDF is not available for this announcement'
          });
        }
      } catch (error) {
        toast.error({
          title: 'Error',
          description: 'Failed to get PDF URL'
        });
      }
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchAnnouncements(newPage);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Build unique company list from stocks and announcements
  const companySuggestions = useMemo(() => {
    const companies = new Set();
    
    // Add from selected stocks
    selectedStocks.forEach(stock => {
      if (stock.name) {
        companies.add(stock.name);
      }
    });
    
    // Add from announcements
    announcements.forEach(announcement => {
      if (announcement.company_name) {
        companies.add(announcement.company_name);
      }
    });
    
    return Array.from(companies).sort();
  }, [selectedStocks, announcements]);

  // Filter suggestions based on user input
  const filteredSuggestions = useMemo(() => {
    const searchTerm = filters.companyName.toLowerCase().trim();
    
    // Only show suggestions if user has typed at least 3 characters
    if (searchTerm.length < 3) {
      return [];
    }
    
    return companySuggestions
      .filter(company => company.toLowerCase().includes(searchTerm))
      .slice(0, 10); // Limit to 10 suggestions
  }, [companySuggestions, filters.companyName]);

  // Handle company name input change
  const handleCompanyNameChange = (value) => {
    setFilters(prev => ({ ...prev, companyName: value }));
    setShowCompanySuggestions(value.length >= 3);
    setSelectedSuggestionIndex(-1);
  };

  // Handle suggestion selection
  const handleSuggestionClick = (company) => {
    setFilters(prev => ({ ...prev, companyName: company }));
    setShowCompanySuggestions(false);
    setSelectedSuggestionIndex(-1);
  };

  // Handle keyboard navigation
  const handleCompanyNameKeyDown = (e) => {
    if (!showCompanySuggestions || filteredSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < filteredSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < filteredSuggestions.length) {
          handleSuggestionClick(filteredSuggestions[selectedSuggestionIndex]);
        }
        break;
      case 'Escape':
        setShowCompanySuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
      default:
        break;
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        companyInputRef.current &&
        !companyInputRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowCompanySuggestions(false);
        setSelectedSuggestionIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const totalSelections = selectedStocks.length + selectedIndexes.length;
  const hasSelections = totalSelections > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
              <span className="inline-block w-3 h-3 bg-red-600 rounded-full animate-pulse"></span>
              <span className="text-red-600">LIVE</span>
              <span>Important Corporate Announcements</span>
            </h1>
            <p className="text-gray-600">
              {loading ? 'Loading...' : `${todaysCount} announcements today`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* WebSocket Connection Status */}
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
              isConnected 
                ? 'bg-green-100 text-green-700' 
                : connectionState === 'connecting' 
                  ? 'bg-yellow-100 text-yellow-700'
                  : 'bg-gray-100 text-gray-600'
            }`}>
              {isConnected ? (
                <>
                  <Wifi size={14} />
                  <span>Live Updates</span>
                </>
              ) : connectionState === 'connecting' ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  <span>Connecting...</span>
                </>
              ) : (
                <>
                  <WifiOff size={14} />
                  <span>Offline</span>
                </>
              )}
            </div>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* How to Use Info */}
      <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
        <p className="text-sm text-blue-800 flex items-start gap-2">
          <span className="text-blue-600 font-bold text-lg">ⓘ</span>
          <span>
            <strong>How to use:</strong> Kindly select stocks from stock search and index search to view in Live corporate announcements.
          </span>
        </p>
      </div>

      {/* Filter Section */}
      <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-3">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Company Name</label>
            <div className="relative" ref={companyInputRef}>
              <input
                type="text"
                value={filters.companyName}
                onChange={(e) => handleCompanyNameChange(e.target.value)}
                onKeyDown={handleCompanyNameKeyDown}
                onFocus={() => filters.companyName.length >= 3 && setShowCompanySuggestions(true)}
                placeholder="Type 3+ chars to search..."
                className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 pr-10 focus:outline-none focus:border-blue-500 transition-colors"
                autoComplete="off"
              />
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              
              {/* Autocomplete Dropdown */}
              {showCompanySuggestions && filteredSuggestions.length > 0 && (
                <div
                  ref={suggestionsRef}
                  className="absolute z-50 w-full mt-1 bg-white border-2 border-blue-500 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                >
                  {filteredSuggestions.map((company, index) => {
                    const searchTerm = filters.companyName.toLowerCase();
                    const companyLower = company.toLowerCase();
                    const startIndex = companyLower.indexOf(searchTerm);
                    
                    return (
                      <div
                        key={index}
                        onClick={() => handleSuggestionClick(company)}
                        className={`px-4 py-2 cursor-pointer transition-colors ${
                          index === selectedSuggestionIndex
                            ? 'bg-blue-100 text-blue-900'
                            : 'hover:bg-gray-100'
                        }`}
                      >
                        {startIndex >= 0 ? (
                          <>
                            {company.substring(0, startIndex)}
                            <span className="font-bold text-blue-600">
                              {company.substring(startIndex, startIndex + filters.companyName.length)}
                            </span>
                            {company.substring(startIndex + filters.companyName.length)}
                          </>
                        ) : (
                          company
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* No results message */}
              {showCompanySuggestions && filters.companyName.length >= 3 && filteredSuggestions.length === 0 && (
                <div
                  ref={suggestionsRef}
                  className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-300 rounded-lg shadow-lg px-4 py-3"
                >
                  <p className="text-sm text-gray-500 text-center">No matching companies found</p>
                </div>
              )}
            </div>
          </div>

          <div className="md:col-span-3">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-blue-500 transition-colors"
            >
              <option value="">--Select Category--</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
              {categories.length === 0 && (
                <>
                  <option value="Company Update">Company Update</option>
                  <option value="Earnings">Earnings</option>
                  <option value="Press Release">Press Release</option>
                  <option value="Investor Presentation">Investor Presentation</option>
                </>
              )}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-blue-500 transition-colors"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:border-blue-500 transition-colors"
            />
          </div>

          <div className="md:col-span-2 flex items-end gap-2">
            <button 
              onClick={handleApplyFilters}
              disabled={loading}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Filter size={18} />
              Filter
            </button>
            <button 
              onClick={handleClearFilters}
              className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors font-semibold flex items-center justify-center gap-2"
            >
              <X size={18} />
              Clear
            </button>
          </div>
        </div>

        {/* Results & Filings preference */}
        <div className="mt-4 pt-4 border-t border-gray-200 flex items-start justify-between gap-4">
          <div>
            <div className="font-semibold text-gray-900">Results &amp; Filings</div>
            <p className="text-sm text-gray-600 mt-1">
              {RESULTS_FILINGS_LABELS.join(' · ')}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Turning this off hides these five types from companies <strong>outside</strong> your
              watchlist. Your selected stocks are unaffected — you'll keep getting everything
              from them.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={resultsFilingsEnabled}
            aria-label="Results and Filings announcements"
            onClick={handleResultsFilingsToggle}
            disabled={resultsFilingsSaving}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
              resultsFilingsEnabled ? 'bg-blue-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                resultsFilingsEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Confirmation shown only when switching off would empty the feed */}
      {showResultsFilingsConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              Turn off Results &amp; Filings?
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              You'll stop seeing and receiving these for companies you haven't selected:
            </p>
            <ul className="text-sm text-gray-700 list-disc list-inside mb-3 space-y-1">
              {RESULTS_FILINGS_LABELS.map((label) => (
                <li key={label}>{label}</li>
              ))}
            </ul>
            <p className="text-sm text-gray-600 mb-4">
              Your watchlist stocks keep sending you everything — but you haven't selected any
              stocks yet, so your LIVE ICA feed will be empty.
            </p>
            <div className="flex gap-2 justify-end">
              <Link
                to="/dashboard/stocks"
                className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
              >
                Select Stocks
              </Link>
              <button
                onClick={() => {
                  setShowResultsFilingsConfirm(false);
                  saveResultsFilingsPreference(false);
                }}
                className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition-colors"
              >
                Turn Off Anyway
              </button>
              <button
                onClick={() => setShowResultsFilingsConfirm(false)}
                className="px-4 py-2 rounded-lg text-gray-600 font-semibold hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Your BSE Announcements Selections */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <span className="text-blue-600">ⓘ</span>
            {isBasicTier ? 'Your Default Index' : 'Your BSE Announcements Selections'}
          </h3>
          {!isBasicTier && (
            <button 
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-blue-600 hover:text-blue-800 font-semibold text-sm flex items-center gap-1"
            >
              ({selectedStocks.length} Stocks + {selectedIndexes.length} Index)
              <span className="ml-2 flex items-center gap-1">
                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                {isExpanded ? 'Collapse' : 'Expand'}
              </span>
            </button>
          )}
        </div>
        <p className="text-sm text-blue-800 mb-4">
          {isBasicTier 
            ? `You are receiving BSE announcements for companies in the ${defaultIndex || 'NIFTY 500'} index.`
            : 'You are receiving BSE announcements for the companies below based on your stock and index selections.'
          }
        </p>

        {selectionsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="animate-spin text-blue-600" size={24} />
            <span className="ml-2 text-gray-600">Loading selections...</span>
          </div>
        ) : !hasSelections ? (
          // Show different message for basic tier users vs users who can customize
          isBasicTier ? (
            <div className="text-center py-8 bg-blue-50 rounded-lg border border-blue-200">
              <Info className="mx-auto text-blue-600 mb-2" size={32} />
              <p className="text-blue-800 font-medium">You're viewing announcements from {defaultIndex || 'NIFTY 500'}</p>
              <p className="text-blue-600 text-sm mt-1">
                Upgrade to Advance or Advance+ plan to customize your stock and index selections
              </p>
              <Link 
                to="/pricing" 
                className="inline-block mt-3 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
              >
                Upgrade Plan
              </Link>
            </div>
          ) : (
            <div className="text-center py-8 bg-yellow-50 rounded-lg border border-yellow-200">
              <AlertCircle className="mx-auto text-yellow-600 mb-2" size={32} />
              <p className="text-yellow-800 font-medium">No stocks or indexes selected</p>
              <p className="text-yellow-600 text-sm mt-1">Please select stocks or indexes to see live announcements</p>
            </div>
          )
        ) : (
          <>
            {/* Selected Indexes */}
            {selectedIndexes.length > 0 && (
              <div className={`mb-6 ${!isExpanded && 'max-h-20 overflow-hidden'}`}>
                <h4 className="text-sm font-bold text-green-700 mb-3 flex items-center gap-2">
                  ☰ Selected Indexes ({selectedIndexes.length})
                </h4>
                <div className="flex flex-wrap gap-2 mb-2">
                  {selectedIndexes.map((index) => (
                    <div key={index.id} className="bg-green-600 text-white px-4 py-2 rounded-lg font-semibold text-sm">
                      = {index.name}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  → These indexes provide access to all their member companies' announcements
                </p>
              </div>
            )}

            {/* Selected Stocks */}
            {selectedStocks.length > 0 && (
              <div className={!isExpanded && selectedIndexes.length > 0 ? 'hidden' : ''}>
                <h4 className="text-sm font-bold text-blue-700 mb-3 flex items-center gap-2">
                  ⊞ Selected Custom Stocks ({selectedStocks.length})
                </h4>
                <div className={`flex flex-wrap gap-2 mb-2 ${!isExpanded && 'max-h-12 overflow-hidden'}`}>
                  {selectedStocks.map((stock) => (
                    <div key={stock.id} className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold text-sm">
                      ⊞ {stock.name}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  → These are your individually selected companies for announcements
                </p>
              </div>
            )}
          </>
        )}

        {/* Manage Buttons - Only show for users who can customize */}
        {canCustomize && (
          <div className="flex gap-3 mt-6">
            <Link
              to="/dashboard/stocks"
              className="border-2 border-gray-800 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-800 hover:text-white transition-all font-semibold"
            >
              ✓ Manage Stocks
            </Link>
            <Link
              to="/dashboard/index-search"
              className="border-2 border-green-600 text-green-600 px-6 py-2 rounded-lg hover:bg-green-600 hover:text-white transition-all font-semibold"
            >
              ✓ Manage Indexes
            </Link>
          </div>
        )}
      </div>

      {/* Announcements Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin text-blue-600" size={32} />
            <span className="ml-3 text-gray-600">Loading announcements...</span>
          </div>
        ) : announcements.length === 0 ? (
          <div className="text-center py-16">
            <AlertCircle className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No announcements found</h3>
            <p className="text-gray-500">
              {hasSelections 
                ? 'No announcements match your current filters. Try adjusting your search criteria.'
                : 'Please select stocks or indexes to see live announcements.'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">DATE</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">SCRIP CODE</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">COMPANY NAME</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">ANNOUNCEMENT</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">HEADLINE</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">PDF</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase">CATEGORY</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {announcements.map((announcement) => (
                    <tr key={announcement.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                        {announcement.display_date || formatDate(announcement.created_at)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 font-mono whitespace-nowrap">
                        {announcement.scrip_code || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                        {announcement.company_name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 max-w-md">
                        {announcement.announcement_summary || announcement.announcement?.substring(0, 200)}
                        {announcement.announcement?.length > 200 && '...'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs">
                        {announcement.headline}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleReadPDF(announcement)}
                          disabled={!announcement.has_pdf && !announcement.pdf_url}
                          className="bg-gray-100 border border-gray-300 text-gray-700 p-2 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="View PDF"
                        >
                          <Eye size={16} />
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {announcement.category_name || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Showing page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Wrapper component with access control
const LiveAnnouncements = () => {
  return (
    <UpgradeOverlay service="live_ica">
      <LiveAnnouncementsContent />
    </UpgradeOverlay>
  );
};

export default LiveAnnouncements;
